
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import GoogleSvg from '~/svg/google.svg';
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/nano-banana';
const DEFAULT_GENERATOR_URL = '/ai-images?model=Nano%20Banana%202.1';
const EDITOR_URL = '/ai-images?action=edit-image&editModel=Nano%20Banana%202.1';
const MODEL_URLS = {
    latest: DEFAULT_GENERATOR_URL,
    lite: '/ai-images?model=Nano%20Banana%202%20Lite',
    standard: '/ai-images?model=Nano%20Banana%202',
    pro: '/ai-images?model=Nano%20Banana%20Pro',
};
type ShowcaseImage = {
    src: string;
    width: number;
    height: number;
    model: string;
    title: string;
    description: string;
};
/**
 * Generated at 1K through Easy-Peasy's image API (Lite, 2 and Pro on
 * 2026-08-30, Nano Banana 2.1 on 2026-10-07 with the same prompt and 4:5
 * size), then re-encoded as WebP and copied to the R2 `images` bucket under
 * `landing/nano-banana/generated/`.
 */
const SAME_PROMPT_OUTPUTS: ShowcaseImage[] = [
    {
        src: `${SHOWCASE_CDN}/generated/same-prompt-nano-banana-2-1.webp`,
        width: 928,
        height: 1152,
        model: 'Nano Banana 2.1',
        title: 'The cleanest copy',
        description: 'Kept the headline and flavor line word for word with no extra text anywhere, and gave the can a bold vertical NANA wordmark.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/same-prompt-lite.webp`,
        width: 928,
        height: 1152,
        model: 'Nano Banana 2 Lite',
        title: 'The fastest draft',
        description: 'Kept the requested headline, product, palette, and layout, while adding a second flavor line and small can copy.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/same-prompt-nano-banana-2.webp`,
        width: 928,
        height: 1152,
        model: 'Nano Banana 2',
        title: 'The balanced result',
        description: 'Delivered clean product photography, strong hierarchy, and accurate headline text with one line of additional package copy.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/same-prompt-pro.webp`,
        width: 928,
        height: 1152,
        model: 'Nano Banana Pro',
        title: 'The controlled finish',
        description: 'Followed the requested copy most closely and added the most distinctive custom lettering and campaign art direction.',
    },
];
const MODEL_GUIDES = [
    {
        id: 'nano-banana-2-1',
        number: '01',
        label: 'New · best value',
        name: 'Nano Banana 2.1',
        technicalName: 'Gemini Nano Banana 2.1',
        headline: 'Better images at half the price',
        description: "Google's newest Nano Banana improves on Nano Banana 2 in visual design, text and infographic layouts, and character consistency, with more natural-looking photos. It generates and edits at 1K, 2K, or 4K with Google web and image-search grounding.",
        recommendation: 'Choose it for most generation and editing work: campaigns, posters, infographics, product shots, and edits that keep people and objects consistent.',
        stats: [
            ['1K–4K', 'output'],
            ['Grounded', 'knowledge'],
            ['2–6', 'credits'],
        ],
        image: `${SHOWCASE_CDN}/generated/nano-banana-2-1-recipe-card.webp`,
        imageWidth: 928,
        imageHeight: 1152,
        imageAlt: 'Banana oat pancakes recipe card with an ingredient list and numbered steps, generated with Nano Banana 2.1',
        href: MODEL_URLS.latest,
        linkLabel: 'Create with Nano Banana 2.1',
        background: 'bg-[#5b43d6]',
        foreground: 'text-[#fbf8ef]',
        secondary: 'text-[#e4dcff]',
    },
    {
        id: 'nano-banana-2-lite',
        number: '02',
        label: 'Fastest',
        name: 'Nano Banana 2 Lite',
        technicalName: 'Gemini 3.1 Flash-Lite Image',
        headline: 'Move at the speed of the idea',
        description: 'Use Lite for rapid visual exploration, thumbnails, social variations, and high-volume pipelines. It generates and edits at 1K, typically in about four seconds, for two Easy-Peasy credits.',
        recommendation: 'Choose it when making more good options matters more than squeezing every last detail into one frame.',
        stats: [
            ['1K', 'output'],
            ['~4 sec', 'generation'],
            ['2', 'credits'],
        ],
        image: `${SHOWCASE_CDN}/generated/lite-ceramic-robot.webp`,
        imageWidth: 928,
        imageHeight: 1152,
        imageAlt: 'Nano Banana 2 Lite output showing a small ceramic robot painting a yellow studio wall',
        href: MODEL_URLS.lite,
        linkLabel: 'Create with Nano Banana 2 Lite',
        background: 'bg-[#f3b736]',
        foreground: 'text-[#17213a]',
        secondary: 'text-[#3e3f37]',
    },
    {
        id: 'nano-banana-2',
        number: '03',
        label: 'Previous generation',
        name: 'Nano Banana 2',
        technicalName: 'Gemini 3.1 Flash Image',
        headline: 'Pro-level capability at Flash speed',
        description: 'Nano Banana 2 is the model Nano Banana 2.1 builds on. It combines precise instructions, readable text, subject consistency, and web plus image-search grounding with output from 0.5K to 4K.',
        recommendation: 'Choose it when a project needs to match images you already made with Nano Banana 2.',
        stats: [
            ['0.5K–4K', 'output'],
            ['Grounded', 'knowledge'],
            ['3–8', 'credits'],
        ],
        image: '/images/nano-banana/nano-banana-2-family-v2.webp',
        imageWidth: 928,
        imageHeight: 1152,
        imageAlt: 'Coral bicycle in a Mediterranean courtyard generated with Nano Banana 2',
        href: MODEL_URLS.standard,
        linkLabel: 'Create with Nano Banana 2',
        background: 'bg-[#d9cffc]',
        foreground: 'text-[#17213a]',
        secondary: 'text-[#4f5062]',
    },
    {
        id: 'nano-banana-pro',
        number: '04',
        label: 'Maximum control',
        name: 'Nano Banana Pro',
        technicalName: 'Gemini 3 Pro Image',
        headline: 'When the image has a real job to do',
        description: 'Pro is built for complex professional assets. Its deeper reasoning, Google Search grounding, typography, localization, reference handling, and 4K delivery make it the deliberate choice for final campaign work.',
        recommendation: 'Choose it for brand systems, detailed diagrams, packaging, multi-element compositions, and edits where precision outranks latency.',
        stats: [
            ['1K–4K', 'output'],
            ['Studio', 'control'],
            ['7–12', 'credits'],
        ],
        image: '/images/nano-banana/nano-banana-pro-family-v2.webp',
        imageWidth: 928,
        imageHeight: 1152,
        imageAlt: 'Midnight-blue fashion campaign generated with Nano Banana Pro',
        href: MODEL_URLS.pro,
        linkLabel: 'Create with Nano Banana Pro',
        background: 'bg-[#17213a]',
        foreground: 'text-[#f7f2e8]',
        secondary: 'text-[#c9ceda]',
    },
];
// Columns: label, Nano Banana 2.1, 2 Lite, 2, Pro.
const COMPARISON_ROWS = [
    [
        'Best for',
        'Most generation and editing',
        'Rapid drafts and volume',
        'Matching earlier Nano Banana 2 work',
        'Complex production assets',
    ],
    [
        'Google model',
        'Gemini Nano Banana 2.1',
        'Gemini 3.1 Flash-Lite Image',
        'Gemini 3.1 Flash Image',
        'Gemini 3 Pro Image',
    ],
    [
        'Input',
        'Text and images',
        'Text and images',
        'Text and images',
        'Text and images',
    ],
    [
        'Easy-Peasy output',
        '1K, 2K, or 4K',
        '1K',
        '0.5K, 1K, 2K, or 4K',
        '1K, 2K, or 4K',
    ],
    [
        'Search grounding',
        'Google web + image search',
        'Not available',
        'Google web + image search',
        'Google Search',
    ],
    [
        'Text in images',
        'Sharper text and infographic layouts',
        'Legible, draft-ready',
        'Precise and localization-ready',
        'Most controlled for dense layouts',
    ],
    [
        'Reference sweet spot',
        'Consistent characters across many references',
        'Fast object-led edits',
        'Multiple objects and characters',
        'Brand, style, and character control',
    ],
    [
        'Easy-Peasy credits',
        '2 / 3 / 6 by resolution',
        '2 at 1K',
        '3 / 4 / 6 / 8 by resolution',
        '7 at 1K–2K · 12 at 4K',
    ],
];
const CAPABILITIES = [
    {
        number: '01',
        title: 'Generate from a detailed brief',
        description: 'Describe the subject, composition, camera, lighting, materials, exact words, and intended use. Gemini understands the relationship between those details instead of treating them as a loose keyword list.',
    },
    {
        number: '02',
        title: 'Edit with natural language',
        description: 'Upload an image or several references and say what should change. Restyle a room, replace a product, alter the lighting, translate a poster, or carry a character into a new scene.',
    },
    {
        number: '03',
        title: 'Render and localize text',
        description: 'Create posters, packaging, menus, diagrams, and social graphics with words in the image. Nano Banana 2.1, Nano Banana 2, and Pro can also translate existing designs while preserving the visual system.',
    },
    {
        number: '04',
        title: 'Use real-world knowledge',
        description: 'Nano Banana 2.1, Nano Banana 2, and Pro can ground a generation with Google Search. That helps with recognizable places, factual diagrams, current references, and visuals that depend on more than style alone.',
    },
];
const WORKFLOW_STEPS = [
    {
        step: '1',
        title: 'Choose the right model',
        description: 'Start with Lite for velocity, Nano Banana 2.1 for balance, or Pro for maximum control.',
    },
    {
        step: '2',
        title: 'Write or upload',
        description: 'Describe the finished image in plain language. Add references when identity, style, layout, or an existing photo should carry through.',
    },
    {
        step: '3',
        title: 'Set the format and create',
        description: 'Pick an aspect ratio and available resolution, generate, then refine the result with another prompt or image edit.',
    },
];
const FAQS = [
    {
        question: 'What is Nano Banana?',
        answer: "Nano Banana is Google's name for the Gemini family of native image-generation and editing models. The current family includes Nano Banana 2.1, Nano Banana 2 Lite, Nano Banana 2, Nano Banana Pro, and the legacy original Nano Banana.",
    },
    {
        question: 'What is Nano Banana 2.1?',
        answer: 'Nano Banana 2.1 is the newest model in the family, released by Google on October 6, 2026 as the successor to Nano Banana 2. Google reports better visual quality and more natural-looking images, sharper text and infographic layouts, stronger character consistency, and fewer artifacts on very wide and very tall formats at 2K and 4K. On Easy-Peasy it costs half as much as Nano Banana 2.',
    },
    {
        question: 'Is Nano Banana 2 the same as Gemini 3.1 Flash Image?',
        answer: 'Yes. Nano Banana 2 is the public name for Gemini 3.1 Flash Image. Nano Banana 2 Lite is Gemini 3.1 Flash-Lite Image, Nano Banana Pro is Gemini 3 Pro Image, and Nano Banana 2.1 is simply called Gemini Nano Banana 2.1.',
    },
    {
        question: 'What is the difference between Nano Banana 2.1, Nano Banana 2, Nano Banana Pro, and Nano Banana 2 Lite?',
        answer: 'Lite prioritizes the lowest latency. Nano Banana 2.1 is the all-rounder with the best balance of quality, speed, and price, and it replaces Nano Banana 2 for most work. Nano Banana 2 stays available for matching earlier results. Pro prioritizes deeper reasoning and precise control for complex professional assets.',
    },
    {
        question: 'Which Nano Banana model should I use?',
        answer: 'Use Nano Banana 2.1 for most work. Pick Lite for rapid ideation, thumbnails, and high-volume variants. Move to Pro for brand systems, dense layouts, demanding reference workflows, or final 4K production assets.',
    },
    {
        question: 'Which Nano Banana model is fastest?',
        answer: 'Nano Banana 2 Lite is the fastest model in the family. Google reports about four seconds for a 1K text-to-image output, making it the best fit for rapid iteration and scale.',
    },
    {
        question: 'Can the Nano Banana models edit existing images?',
        answer: 'Yes. All four models on this page support image editing. Upload one or more images, describe the intended change in plain language, and choose a model based on the speed and control you need.',
    },
    {
        question: 'Can Nano Banana generate readable text?',
        answer: 'Yes. The family is designed to render legible text for posters, packaging, diagrams, menus, and marketing assets. Always proofread important names, prices, instructions, and factual claims before publishing.',
    },
    {
        question: 'What resolutions and aspect ratios are available?',
        answer: 'On Easy-Peasy, Lite generates at 1K; Nano Banana 2.1 offers 1K, 2K, and 4K; Nano Banana 2 offers 0.5K, 1K, 2K, and 4K; and Pro offers 1K, 2K, and 4K. Standard formats include 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9, and 21:9. Image edits can also match the source.',
    },
    {
        question: 'How much do the Nano Banana models cost on Easy-Peasy?',
        answer: 'Nano Banana 2.1 uses 2 credits at 1K, 3 at 2K, and 6 at 4K. Nano Banana 2 Lite uses 2 credits. Nano Banana 2 uses 3 credits at 0.5K, 4 at 1K, 6 at 2K, and 8 at 4K. Nano Banana Pro uses 7 credits at 1K or 2K and 12 credits at 4K.',
    },
    {
        question: 'Do the Nano Banana models support reference images?',
        answer: 'Yes. The Gemini Image family supports multiple mixed references. Nano Banana 2.1 and Nano Banana 2 are especially useful for combining objects and maintaining characters, with 2.1 keeping characters more consistent, while Pro adds stronger brand, style, and precision-control workflows.',
    },
    {
        question: 'Does Nano Banana use Google Search?',
        answer: 'Nano Banana 2.1 and Nano Banana 2 can use Google web and image-search grounding, and Nano Banana Pro can use Google Search grounding. Nano Banana 2 Lite does not support Search grounding.',
    },
    {
        question: 'Are Nano Banana images watermarked?',
        answer: 'Google says images generated by its Nano Banana models include SynthID, an invisible watermark designed to help identify AI-generated media.',
    },
    {
        question: 'How were the examples on this page created?',
        answer: 'Every example was generated through the Easy-Peasy image API. The four poster outputs used the same prompt, aspect ratio, and 1K resolution so visitors can compare the models on equal footing.',
    },
];
const CheckIcon = () => (<svg className='h-4 w-4 flex-none' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
    <path d='m4 10.5 3.6 3.6L16 5.7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>);
const ArrowIcon = () => (<svg className='h-5 w-5 transition-transform duration-200 group-hover:translate-x-1' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
    <path d='M3 10h13m-5-5 5 5-5 5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>);
export default function NanoBananaLandingPage() {
    const { t } = useLocale('nano-banana');
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: t(faq.question),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t(faq.answer),
            },
        })),
    };
    const pageSchema = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: t('Home'),
                        item: 'https://easy-peasy.ai',
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('AI Image Generator'),
                        item: 'https://easy-peasy.ai/ai-image-generator',
                    },
                    {
                        '@type': 'ListItem',
                        position: 3,
                        name: t('Nano Banana AI Image Models'),
                        item: 'https://easy-peasy.ai/ai-image-generator/nano-banana',
                    },
                ],
            },
            {
                '@type': 'ItemList',
                name: t('Nano Banana image model family'),
                numberOfItems: MODEL_GUIDES.length,
                itemListElement: MODEL_GUIDES.map((model, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'SoftwareApplication',
                        name: model.name,
                        applicationCategory: 'MultimediaApplication',
                        operatingSystem: 'Web Browser',
                        url: `https://easy-peasy.ai/ai-image-generator/nano-banana#${model.id}`,
                    },
                })),
            },
        ],
    };
    return (<>
      <Seo templateTitle={t('Nano Banana 2.1, Nano Banana 2, Pro & Lite')} description={t("Try Google's new Nano Banana 2.1 and compare it with Nano Banana 2, Nano Banana Pro, and Nano Banana 2 Lite. See real 1K outputs, features, resolutions, and credits, then create or edit images online.")} image='https://easy-peasy.ai/images/nano-banana/nano-banana-pro-hero-v2.webp' page='product' productName={t('Nano Banana AI Image Generator')} productDescription={t('Generate and edit images with Nano Banana 2.1, Nano Banana 2, Nano Banana Pro, and Nano Banana 2 Lite on Easy-Peasy.AI.')} robots='follow, index' productImage={[
            `${SHOWCASE_CDN}/generated/nano-banana-2-1-recipe-card.webp`,
            'https://easy-peasy.ai/images/nano-banana/nano-banana-pro-hero-v2.webp',
            'https://easy-peasy.ai/images/nano-banana/nano-banana-2-family-v2.webp',
            `${SHOWCASE_CDN}/generated/lite-ceramic-robot.webp`,
        ]}/>
      <script id='nano-banana-faq-schema' type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}/>
      <script id='nano-banana-page-schema' type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}/>

      <main className='overflow-hidden bg-[#f7f2e8] text-[#17213a]'>
        {/* Hero */}
        <section className='relative isolate min-h-screen overflow-hidden'>
          <div className='absolute inset-x-0 top-0 z-30'>
            <LandingPageNavigation />
          </div>

          <div className='pointer-events-none absolute inset-0 opacity-70' aria-hidden='true' style={{
            backgroundImage: 'linear-gradient(rgba(23,33,58,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(23,33,58,0.04) 1px, transparent 1px)',
            backgroundSize: '52px 52px',
        }}/>
          <div className='pointer-events-none absolute -right-32 top-24 h-[32rem] w-[32rem] rounded-full bg-[#f3b736]/35 blur-3xl' aria-hidden='true'/>

          <div className='relative mx-auto grid min-h-screen max-w-7xl items-center gap-16 px-6 pb-20 pt-36 lg:grid-cols-[0.88fr_1.12fr] lg:px-8 lg:pb-24 lg:pt-40'>
            <div className='relative z-10 max-w-2xl'>
              <a href='#nano-banana-2-1' className='mb-4 flex w-fit items-center gap-3 rounded-full bg-[#17213a] py-1.5 pl-1.5 pr-4 text-sm font-bold text-[#f7f2e8] shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b43d6]'>
                <span className='rounded-full bg-[#f3b736] px-2.5 py-1 text-xs font-black uppercase tracking-[0.08em] text-[#17213a]'>
                  {t('New')}
                </span>
                {t('Nano Banana 2.1 is here, at half the price of Nano Banana 2')}
              </a>
              <div className='mb-8 inline-flex items-center gap-3 rounded-full border border-[#17213a]/10 bg-white/80 py-2 pl-2 pr-4 text-sm font-bold text-[#39445d] shadow-sm'>
                <span className='flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-[#17213a]/10'>
                  <GoogleSvg className='h-5 w-5' aria-hidden='true'/>
                </span>
                {t("Google's Gemini Image family")}
              </div>

              <div className='mb-5 flex items-center gap-3 text-sm font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                <span className='h-px w-12 bg-[#5b43d6]' aria-hidden='true'/>
                {t('Generate · edit · iterate')}
              </div>

              <h1 className='max-w-[10ch] text-[clamp(4.4rem,8.5vw,8rem)] font-black leading-[0.82] tracking-[-0.07em] text-[#17213a]'>
                <span className='block'>Nano</span>
                <span className='block'>Banana</span>
              </h1>
              <div className='mt-5 flex flex-wrap items-center gap-2 text-sm font-black uppercase tracking-[0.12em] sm:text-base'>
                <a href='#nano-banana-2-1' className='rounded-full bg-[#5b43d6] px-4 py-2 text-white transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b43d6]'>
                  2.1
                </a>
                <a href='#nano-banana-2' className='rounded-full bg-[#d9cffc] px-4 py-2 text-[#17213a] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b43d6]'>
                  2
                </a>
                <a href='#nano-banana-pro' className='rounded-full bg-[#17213a] px-4 py-2 text-[#f7f2e8] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b43d6]'>
                  Pro
                </a>
                <a href='#nano-banana-2-lite' className='rounded-full bg-[#f3b736] px-4 py-2 text-[#17213a] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5b43d6]'>
                  2 Lite
                </a>
              </div>

              <p className='mt-8 max-w-xl text-xl font-semibold leading-8 text-[#25304b] sm:text-2xl'>
                {t('Four models. One visual language. The right speed for every idea.')}
              </p>
              <p className='mt-4 max-w-xl text-base leading-7 text-[#5a6275] sm:text-lg'>
                {t('Create and edit with the complete Nano Banana family in one studio. Start with Lite for speed, use Nano Banana 2.1 for the best everyday balance, or choose Pro when precision matters most.')}
              </p>

              <div className='mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center'>
                <Link href={DEFAULT_GENERATOR_URL} className='group inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full bg-[#17213a] px-7 py-3 text-base font-bold text-[#f8f4ea] shadow-[0_8px_0_#5b43d6] transition-transform duration-200 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#5b43d6]'>
                  {t('Create with Nano Banana 2.1')}
                  <ArrowIcon />
                </Link>
                <Link href={EDITOR_URL} className='group inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full border-2 border-[#17213a] bg-white/60 px-7 py-3 text-base font-bold text-[#17213a] transition-colors duration-200 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#5b43d6]'>
                  {t('Edit an image')}
                  <ArrowIcon />
                </Link>
              </div>

              <div className='mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-[#5a6275]'>
                {[
            'Text + image input',
            'Natural-language edits',
            'Up to 4K output',
        ].map((item) => (<span key={item} className='inline-flex items-center gap-2'>
                    <span className='text-[#5b43d6]'>
                      <CheckIcon />
                    </span>
                    {t(item)}
                  </span>))}
              </div>
            </div>

            <div className='relative mx-auto min-h-[520px] w-full max-w-2xl sm:min-h-[650px] lg:min-h-[720px]'>
              <figure className='absolute right-[2%] top-[2%] w-[63%] overflow-hidden rounded-[2rem] border-[8px] border-white bg-white shadow-[0_30px_80px_rgba(23,33,58,0.2)]'>
                <div className='relative aspect-[4/5]'>
                  <Image src={`${SHOWCASE_CDN}/generated/same-prompt-nano-banana-2-1.webp`} alt={t('NANA sparkling citrus product poster generated with Nano Banana 2.1')} fill priority sizes='(max-width: 1024px) 62vw, 34vw' className='object-cover'/>
                </div>
                <figcaption className='absolute bottom-4 left-4 rounded-full bg-[#5b43d6] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white shadow-lg'>
                  Nano Banana 2.1
                </figcaption>
              </figure>

              <figure className='absolute left-0 top-[14%] w-[37%] -rotate-[3deg] overflow-hidden rounded-[1.5rem] border-[7px] border-white bg-white shadow-[0_20px_55px_rgba(23,33,58,0.2)]'>
                <div className='relative aspect-[4/5]'>
                  <Image src={`${SHOWCASE_CDN}/generated/lite-ceramic-robot.webp`} alt={t('Ceramic robot painting a studio wall, generated with Nano Banana 2 Lite')} fill sizes='(max-width: 1024px) 37vw, 20vw' className='object-cover'/>
                </div>
                <figcaption className='absolute bottom-3 left-3 rounded-full bg-[#f3b736] px-3 py-2 text-[0.65rem] font-black uppercase tracking-[0.1em] text-[#17213a] shadow-lg'>
                  2 Lite
                </figcaption>
              </figure>

              <figure className='absolute bottom-[2%] left-[7%] w-[80%] rotate-[1.5deg] overflow-hidden rounded-[1.8rem] border-[8px] border-white bg-white shadow-[0_24px_70px_rgba(23,33,58,0.22)]'>
                <div className='relative aspect-video'>
                  <Image src='/images/nano-banana/nano-banana-pro-hero-v2.webp' alt={t('Indigo glass and leather campaign still life generated with Nano Banana Pro')} fill sizes='(max-width: 1024px) 78vw, 43vw' className='object-cover'/>
                </div>
                <figcaption className='absolute bottom-3 right-3 rounded-full bg-[#17213a] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white shadow-lg'>
                  Nano Banana Pro
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Quick chooser */}
        <section className='border-y border-[#17213a]/10 bg-white/70'>
          <div className='mx-auto grid max-w-7xl divide-y divide-[#17213a]/10 px-6 lg:grid-cols-4 lg:divide-x lg:divide-y-0 lg:px-8'>
            {[
            [
                '2.1',
                'The new all-rounder',
                'For most creative work',
                '#5b43d6',
            ],
            ['Lite', 'About 4 seconds', 'For drafts and scale', '#f3b736'],
            [
                '2',
                'The previous all-rounder',
                'For matching earlier results',
                '#d9cffc',
            ],
            ['Pro', 'Maximum control', 'For final production', '#17213a'],
        ].map(([name, value, label, color]) => (<div key={name} className='flex items-center gap-5 px-2 py-8 lg:px-7 lg:py-10'>
                <span className='flex h-14 w-14 flex-none items-center justify-center rounded-full text-lg font-black' style={{
                backgroundColor: color,
                color: name === 'Pro' || name === '2.1' ? '#f7f2e8' : '#17213a',
            }}>
                  {name}
                </span>
                <div>
                  <div className='font-black text-[#17213a]'>{t(value)}</div>
                  <div className='mt-1 text-sm text-[#6a7182]'>{t(label)}</div>
                </div>
              </div>))}
          </div>
        </section>

        {/* Same-prompt comparison */}
        <section id='examples' className='bg-[#17213a] py-24 text-[#f7f2e8] sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='grid gap-10 border-b border-[#f7f2e8]/15 pb-12 lg:grid-cols-[1fr_0.8fr] lg:items-end lg:gap-20'>
              <div className='max-w-3xl'>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#c9bcff]'>
                  {t('Real Easy-Peasy outputs')}
                </p>
                <h2 className='mt-5 text-4xl font-black tracking-[-0.045em] sm:text-6xl'>
                  {t('Same brief. Same size. Four different decisions.')}
                </h2>
              </div>
              <div className='rounded-2xl bg-[#f3b736] p-6 text-[#17213a] shadow-[8px_8px_0_#5b43d6]'>
                <p className='text-xs font-black uppercase tracking-[0.16em]'>
                  {t('The test')}
                </p>
                <p className='mt-3 text-sm font-semibold leading-6'>
                  {t('A premium NANA citrus-drink poster with one exact headline, one exact flavor line, a yellow can, cobalt pedestal, leaf shadows, and no extra words. All outputs: 4:5 at 1K.')}
                </p>
              </div>
            </div>

            <div className='mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
              {SAME_PROMPT_OUTPUTS.map((item) => (<figure key={item.src} className='group'>
                  <div className='relative overflow-hidden rounded-[1.5rem] bg-[#222e4b]'>
                    <Image src={item.src} alt={t(`${item.model} sample output for the NANA sparkling citrus poster comparison`)} width={item.width} height={item.height} sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw' className='h-auto w-full transition-transform duration-500 group-hover:scale-[1.015]'/>
                    <div className='absolute left-4 top-4 rounded-full bg-[#f7f2e8] px-4 py-2 text-xs font-black uppercase tracking-[0.1em] text-[#17213a] shadow-lg'>
                      {item.model}
                    </div>
                  </div>
                  <figcaption className='px-1 pt-6'>
                    <h3 className='text-xl font-black'>{t(item.title)}</h3>
                    <p className='mt-3 text-sm leading-6 text-[#c9ceda]'>
                      {t(item.description)}
                    </p>
                  </figcaption>
                </figure>))}
            </div>

            <details className='group mt-12 border-y border-[#f7f2e8]/20 py-6'>
              <summary className='flex cursor-pointer list-none items-center justify-between gap-6 text-sm font-black marker:content-none'>
                {t('Read the exact comparison prompt')}
                <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[#f7f2e8]/35 text-xl transition-transform duration-200 group-open:rotate-45'>
                  +
                </span>
              </summary>
              <p className='mt-5 max-w-4xl border-l-2 border-[#f3b736] pl-5 text-sm leading-7 text-[#c9ceda]'>
                {t('Create a premium editorial poster for an imaginary sparkling citrus drink named NANA. Center one brushed-aluminum yellow can covered in realistic condensation on a cobalt-blue pedestal, with crisp banana-leaf shadows and a warm cream background. Include this exact headline in large dark navy type: “BRIGHT IDEAS, BOTTLED.” Include this exact smaller line: “YUZU · LIME · ZERO SUGAR”. High-end art direction, clean hierarchy, balanced negative space, realistic product photography, no extra words, no logos besides NANA.')}
              </p>
            </details>
          </div>
        </section>

        {/* Model-by-model guide */}
        <section className='bg-[#f7f2e8] py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='grid gap-8 lg:grid-cols-[0.65fr_1.35fr] lg:gap-20'>
              <div>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                  {t('Choose your model')}
                </p>
                <h2 className='mt-5 max-w-md text-4xl font-black tracking-[-0.045em] text-[#17213a] sm:text-6xl'>
                  {t('The Nano Banana family, without the guesswork')}
                </h2>
              </div>
              <p className='max-w-2xl self-end text-lg leading-8 text-[#626a7c]'>
                {t('The four models share natural-language generation and editing. What changes is how much they prioritize speed, price, reasoning, and production control.')}
              </p>
            </div>

            <div className='mt-16 space-y-8'>
              {MODEL_GUIDES.map((model, index) => (<article key={model.id} id={model.id} className={`${model.background} ${model.foreground} scroll-mt-24 overflow-hidden rounded-[2rem]`}>
                  <div className='grid lg:grid-cols-2'>
                    <div className={`relative min-h-[360px] lg:min-h-[560px] ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <Image src={model.image} alt={t(model.imageAlt)} fill sizes='(max-width: 1024px) 100vw, 50vw' className='object-cover'/>
                    </div>
                    <div className={`flex flex-col justify-between p-8 sm:p-12 lg:p-14 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div>
                        <div className='flex items-center justify-between gap-5 border-b border-current/20 pb-6'>
                          <span className='text-sm font-black tracking-[0.18em]'>
                            {model.number}
                          </span>
                          <span className='rounded-full border border-current/30 px-4 py-2 text-xs font-black uppercase tracking-[0.12em]'>
                            {t(model.label)}
                          </span>
                        </div>
                        <p className={`mt-8 text-sm font-bold ${model.secondary}`}>
                          {model.technicalName}
                        </p>
                        <h3 className='mt-3 text-4xl font-black tracking-[-0.045em] sm:text-5xl'>
                          {model.name}
                        </h3>
                        <p className='mt-5 text-xl font-black leading-8'>
                          {t(model.headline)}
                        </p>
                        <p className={`mt-5 text-base leading-7 ${model.secondary}`}>
                          {t(model.description)}
                        </p>
                        <p className={`mt-4 text-base leading-7 ${model.secondary}`}>
                          {t(model.recommendation)}
                        </p>
                      </div>

                      <div className='mt-10'>
                        <dl className='grid grid-cols-3 border-y border-current/20'>
                          {model.stats.map(([value, label]) => (<div key={label} className='py-5 pr-3'>
                              <dt className='text-lg font-black sm:text-xl'>
                                {value}
                              </dt>
                              <dd className={`mt-1 text-xs uppercase tracking-[0.1em] ${model.secondary}`}>
                                {t(label)}
                              </dd>
                            </div>))}
                        </dl>
                        <Link href={model.href} className={`group mt-8 inline-flex min-h-[50px] items-center justify-center gap-3 rounded-full px-6 py-3 font-black transition-transform hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ${model.name === 'Nano Banana Pro' ||
                model.name === 'Nano Banana 2.1'
                ? 'bg-[#f3b736] text-[#17213a] focus-visible:outline-[#f7f2e8]'
                : 'bg-[#17213a] text-[#f7f2e8] focus-visible:outline-[#5b43d6]'}`}>
                          {t(model.linkLabel)}
                          <ArrowIcon />
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>))}
            </div>
          </div>
        </section>

        {/* Comparison table */}
        <section className='bg-white py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='max-w-4xl'>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                {t('Nano Banana model comparison')}
              </p>
              <h2 className='mt-5 text-4xl font-black tracking-[-0.045em] text-[#17213a] sm:text-6xl'>
                {t('2.1 vs Lite vs 2 vs Pro: specs that change the workflow')}
              </h2>
              <p className='mt-6 max-w-3xl text-lg leading-8 text-[#626a7c]'>
                {t('This table combines the underlying Google model capabilities with the resolutions and credit pricing currently available inside Easy-Peasy.')}
              </p>
            </div>

            <div className='mt-14 overflow-x-auto rounded-[1.75rem] bg-[#f7f2e8] p-2 sm:p-5'>
              <table className='w-full min-w-[1040px] border-collapse text-left'>
                <caption className='sr-only'>
                  {t('Comparison of Nano Banana 2.1, Nano Banana 2 Lite, Nano Banana 2, and Nano Banana Pro')}
                </caption>
                <thead>
                  <tr>
                    <th className='px-5 py-5 text-xs font-black uppercase tracking-[0.14em] text-[#6a7182]'>
                      {t('Compare')}
                    </th>
                    <th className='bg-[#5b43d6] px-5 py-5 text-base font-black text-[#fbf8ef]'>
                      Nano Banana 2.1
                    </th>
                    <th className='bg-[#f3b736] px-5 py-5 text-base font-black text-[#17213a]'>
                      Nano Banana 2 Lite
                    </th>
                    <th className='bg-[#d9cffc] px-5 py-5 text-base font-black text-[#17213a]'>
                      Nano Banana 2
                    </th>
                    <th className='bg-[#17213a] px-5 py-5 text-base font-black text-[#f7f2e8]'>
                      Nano Banana Pro
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map(([label, latest, lite, standard, pro]) => (<tr key={label} className='border-t border-[#17213a]/10'>
                        <th className='px-5 py-5 text-sm font-black text-[#17213a]'>
                          {t(label)}
                        </th>
                        <td className='bg-[#5b43d6] px-5 py-5 text-sm font-semibold text-[#f1edff]'>
                          {t(latest)}
                        </td>
                        <td className='bg-[#f3b736] px-5 py-5 text-sm text-[#38392f]'>
                          {t(lite)}
                        </td>
                        <td className='bg-[#d9cffc] px-5 py-5 text-sm font-semibold text-[#414255]'>
                          {t(standard)}
                        </td>
                        <td className='bg-[#17213a] px-5 py-5 text-sm text-[#d8dbe4]'>
                          {t(pro)}
                        </td>
                      </tr>))}
                </tbody>
              </table>
            </div>

            <div className='mt-8 flex flex-col gap-4 border-l-2 border-[#5b43d6] pl-5 text-sm leading-6 text-[#6a7182] sm:flex-row sm:items-start sm:justify-between'>
              <p className='max-w-3xl'>
                {t('Capability names were verified against Google DeepMind and the Gemini API documentation. Easy-Peasy pricing and available resolutions reflect the live model configuration as of October 7, 2026.')}
              </p>
              <a href='https://ai.google.dev/gemini-api/docs/image-generation' target='_blank' rel='noopener noreferrer' className='flex-none font-bold text-[#5b43d6] underline decoration-[#5b43d6]/30 underline-offset-4 hover:decoration-[#5b43d6]'>
                {t('Read Google model docs')}
              </a>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className='bg-[#f3b736] py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='grid gap-14 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20'>
              <div>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                  {t('What you can make')}
                </p>
                <h2 className='mt-5 max-w-md text-4xl font-black tracking-[-0.045em] text-[#17213a] sm:text-6xl'>
                  {t('Prompt it like a brief. Refine it like a conversation.')}
                </h2>
                <p className='mt-6 max-w-md text-lg leading-8 text-[#45463f]'>
                  {t('Nano Banana is natively multimodal. Words and images belong in the same workflow, so creating and editing feel like two parts of one conversation.')}
                </p>
              </div>

              <div className='border-t-2 border-[#17213a]'>
                {CAPABILITIES.map((capability) => (<div key={capability.number} className='grid gap-4 border-b border-[#17213a]/25 py-8 sm:grid-cols-[4rem_1fr] sm:gap-8'>
                    <span className='text-sm font-black tracking-[0.16em] text-[#5b43d6]'>
                      {capability.number}
                    </span>
                    <div>
                      <h3 className='text-2xl font-black tracking-[-0.025em] text-[#17213a]'>
                        {t(capability.title)}
                      </h3>
                      <p className='mt-3 max-w-2xl text-base leading-7 text-[#45463f]'>
                        {t(capability.description)}
                      </p>
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className='bg-[#5b43d6] py-24 text-[#fbf8ef] sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='max-w-3xl'>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#e4dcff]'>
                {t('From idea to image')}
              </p>
              <h2 className='mt-5 text-4xl font-black tracking-[-0.045em] sm:text-6xl'>
                {t('Three steps. Switch models whenever the work changes.')}
              </h2>
            </div>

            <div className='mt-16 grid border-y border-[#fbf8ef]/35 lg:grid-cols-3 lg:divide-x lg:divide-[#fbf8ef]/35'>
              {WORKFLOW_STEPS.map((item) => (<div key={item.step} className='border-b border-[#fbf8ef]/35 py-9 last:border-b-0 lg:border-b-0 lg:px-9 lg:first:pl-0 lg:last:pr-0'>
                  <div className='flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#fbf8ef] text-lg font-black'>
                    {item.step}
                  </div>
                  <h3 className='mt-8 text-2xl font-black'>{t(item.title)}</h3>
                  <p className='mt-3 max-w-sm text-base leading-7 text-[#e4dcff]'>
                    {t(item.description)}
                  </p>
                </div>))}
            </div>

            <Link href={DEFAULT_GENERATOR_URL} className='group mt-10 inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full bg-[#f3b736] px-7 py-3 font-black text-[#17213a] transition-transform duration-200 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#fbf8ef]'>
              {t('Open the Nano Banana image generator')}
              <ArrowIcon />
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className='bg-[#f7f2e8] py-24 sm:py-32'>
          <div className='mx-auto max-w-4xl px-6 lg:px-8'>
            <div className='text-center'>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                {t('FAQ')}
              </p>
              <h2 className='mt-5 text-4xl font-black tracking-[-0.045em] text-[#17213a] sm:text-6xl'>
                {t('Nano Banana models, explained')}
              </h2>
            </div>

            <div className='mt-14 border-t-2 border-[#17213a]'>
              {FAQS.map((faq) => (<details key={faq.question} className='group border-b border-[#17213a]/20'>
                  <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-left text-lg font-black text-[#17213a] marker:content-none sm:text-xl'>
                    {t(faq.question)}
                    <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[#17213a]/30 text-xl transition-transform duration-200 group-open:rotate-45'>
                      +
                    </span>
                  </summary>
                  <p className='max-w-3xl pb-7 pr-12 text-base leading-7 text-[#626a7c]'>
                    {t(faq.answer)}
                  </p>
                </details>))}
            </div>

            <p className='mt-8 text-center text-xs leading-5 text-[#777e8e]'>
              {t('Google, Gemini, and Nano Banana are trademarks of Google LLC. Easy-Peasy.AI is an independent platform and is not affiliated with or endorsed by Google.')}
            </p>
          </div>
        </section>

        <div className='bg-white dark:bg-slate-900'>
          <FeaturedLogos />
          <HappyUsers />
          <Testimonials />
          <LogosCloud />
          <CallToAction preset='ai-images' href={DEFAULT_GENERATOR_URL}/>
          <Footer />
        </div>
      </main>
    </>);
}
const getStaticProps: GetStaticProps = async () => ({
    props: {},
});

    async function __Next_Translate__getStaticProps__1a13a29a1bb__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-image-generator/nano-banana',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a1bb__ as getStaticProps }
  